import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { LocalizedLink } from '@/components/LocalizedLink';
import { setupCookieBlocking } from '@/lib/cookieManager';
import { initMetaPixel } from '@/lib/metaPixel';
import { initGoogleAnalytics } from '@/lib/googleAnalytics';

export const CookieBanner = () => {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const cookieConsent = sessionStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    if (cookieConsent === 'rejected') {
      setupCookieBlocking();
    }
  }, []);

  const handleAccept = () => {
    sessionStorage.setItem('cookieConsent', 'accepted');
    initMetaPixel();
    initGoogleAnalytics();
    setIsVisible(false);
  };

  const handleReject = () => {
    sessionStorage.setItem('cookieConsent', 'rejected');
    setupCookieBlocking();
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      className="fixed inset-x-0 bottom-0 z-50 box-border w-full max-w-[100vw] p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4 sm:pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="mx-auto box-border w-full max-w-7xl rounded-lg border border-border bg-card/95 p-4 shadow-lg backdrop-blur-sm sm:p-6">
        <h2 id="cookie-banner-title" className="sr-only">
          {t('cookieBanner.title')}
        </h2>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <p id="cookie-banner-description" className="min-w-0 text-sm text-muted-foreground">
            {t('cookieBanner.text')}{' '}
            <LocalizedLink href="/cookie-policy" className="text-primary hover:underline">
              {t('cookieBanner.moreInfo')}
            </LocalizedLink>
          </p>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReject}
              className="w-full border-border text-muted-foreground hover:bg-muted hover:text-foreground sm:w-auto"
            >
              {t('cookieBanner.reject')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleAccept}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 sm:w-auto"
            >
              {t('cookieBanner.accept')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
